import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { inventoryAPI, ownerAccessAPI } from '../api/client';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import useSidebarCollapsed from '../hooks/useSidebarCollapsed';
import AddInventoryForm from '../components/AddInventoryForm';
import InventoryCard from '../components/InventoryCard';
import OwnerAccessSection from '../components/OwnerAccessSection';
import CountdownTimer from '../components/CountdownTimer';
import useRealtimeData from '../hooks/useRealtimeData';
import { useSocket } from '../context/SocketContext';
import { FiX, FiHome, FiMapPin, FiLayers, FiMaximize, FiGrid, FiSun, FiUser, FiPhone, FiCalendar, FiFileText, FiEdit2, FiEye, FiDownload, FiKey, FiTruck, FiCheckCircle, FiDollarSign, FiMessageSquare, FiShield, FiTag, FiBriefcase, FiWifi, FiNavigation, FiBox, FiPackage, FiActivity, FiAward, FiClipboard, FiClock } from 'react-icons/fi';

function InventoryPage() {
  const sidebarCollapsed = useSidebarCollapsed();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);

  const [units, setUnits] = useState([]);
  const [unitThumbnails, setUnitThumbnails] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitMedia, setUnitMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [caption, setCaption] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  const [newUnit, setNewUnit] = useState({});

  // Active media item shown in hero gallery & video modal
  const [activeMediaItem, setActiveMediaItem] = useState(null);
  const [videoModalSrc, setVideoModalSrc] = useState(null);

  // Backend accepts max 10 files per request; keep client-side chunking unlimited.
  const MEDIA_UPLOAD_BATCH_SIZE = 10;

  // Edit state
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [editUnit, setEditUnit] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    query: '',
    project_id: '',
    status: '',
    bhk: '',
    listing_type: '',
    budget_min: '',
    budget_max: '',
    area_min: '',
    area_max: '',
    location: '',
    keys_location: '',
    facing: '',
    furnished_status: ''
  });
  const [hasSearched, setHasSearched] = useState(false);

  // Activity logs state (managers/admin only)
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [inventoryLogs, setInventoryLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const isManager = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  // My Inventory tab state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'my' ? 'my' : 'all');
  const [myUnits, setMyUnits] = useState([]);
  const [myUnitsLoading, setMyUnitsLoading] = useState(false);
  const [myUnitsCount, setMyUnitsCount] = useState(0);

  // My Owner Requests state (employees only)
  const [myOwnerRequests, setMyOwnerRequests] = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [myRequestsExpanded, setMyRequestsExpanded] = useState(true);

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const response = await inventoryAPI.getProjects();
      setProjects(response.data || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  // Fetch activity logs (managers/admin)
  const fetchLogs = async () => {
    if (!isManager) return;
    setLogsLoading(true);
    try {
      const res = await api.get('/inventory/logs?limit=100');
      setInventoryLogs(res.data?.logs || []);
    } catch (err) {
      console.error('Failed to fetch inventory logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Fetch stats (if backend provides)
  const fetchStats = async () => {
    try {
      if (inventoryAPI.getStats) {
        const res = await inventoryAPI.getStats();
        setStats(res.data || null);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch my owner access requests (employees only)
  const fetchMyOwnerRequests = useCallback(async () => {
    if (isManager) return;
    setMyRequestsLoading(true);
    try {
      const res = await ownerAccessAPI.getMyRequests();
      setMyOwnerRequests(res.data?.requests || res.data || []);
    } catch (err) {
      console.error('Failed to fetch my owner requests:', err);
    } finally {
      setMyRequestsLoading(false);
    }
  }, [isManager, user?.permissions]);

  // Fetch my inventory units (units created by current employee)
  const fetchMyUnits = useCallback(async () => {
    setMyUnitsLoading(true);
    try {
      const params = { limit: 0 };
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) return;
        if (key === 'bhk') {
          const raw = filters.bhk.toString();
          const digits = (raw.match(/(\d+)/) || [])[0];
          if (digits) {
            params.bhk = raw.includes('+') ? `${digits}+` : digits;
          } else {
            params.bhk = raw.replace(/\s+/g, '').toLowerCase();
          }
          return;
        }
        params[key] = filters[key];
      });

      const bhkFilterForClient = params.bhk || null;
      if (bhkFilterForClient) delete params.bhk;

      const res = await inventoryAPI.getMyUnits(params);
      const raw = Array.isArray(res.data) ? res.data : (res.data?.units || res.data || []);
      const total = res.data?.total || raw.length;

      // Reuse the same normalize function pattern used for all units
      const normalize = (u) => ({
        _id: u._id || u.id,
        id: u._id || u.id,
        unit_number: u.unit_number || u.unitNumber || u.name || '',
        name: u.name || u.unit_number || u.unitNumber || '',
        location: u.location || u.address || '',
        bhk: u.bhk || '',
        status: u.status || u.state || 'available',
        built_up_area: u.built_up_area || u.builtUpArea || u.carpet_area || '',
        super_area: u.super_area || u.superArea || '',
        base_price: u.base_price || u.basePrice || u.price || 0,
        final_price: u.final_price || u.finalPrice || u.price || 0,
        price_per_sqft: u.price_per_sqft || u.pricePerSqft || 0,
        parking_slots: u.parking_slots || u.parking || u.parkingSlots || 0,
        listing_type: u.listing_type || u.listingType || u.looking_to || '',
        keys_location: u.keys_location || u.keysLocation || '',
        furnished_status: u.furnished_status || u.furnishedStatus || '',
        owner_name: u.owner_name || '',
        owner_phone: u.owner_phone || '',
        availability_date: u.availability_date || u.availabilityDate || '',
        remarks: u.remarks || u.keys_remarks || '',
        thumbnail: null,
        building_name: u.building_name || u.buildingName || u.tower || '',
        project_name: (u.project && (u.project.name || u.project.title)) || u.project_name || '',
        tower: u.tower || '',
        amenities: (() => {
          try {
            if (Array.isArray(u.amenities)) return u.amenities.map(a => typeof a === 'string' ? a : (a.name || a.title || JSON.stringify(a)));
            if (typeof u.amenities === 'string') return u.amenities.split(',').map(s => s.trim()).filter(Boolean);
          } catch (e) {}
          return [];
        })(),
        raw: u
      });

      let normalized = raw.map(normalize);

      // Apply tolerant client-side BHK filtering
      if (bhkFilterForClient) {
        const filterBhkRaw = bhkFilterForClient.toString().toLowerCase();
        const filterNum = (filterBhkRaw.match(/(\d+)/) || [])[0];
        normalized = normalized.filter((u) => {
          const unitBhkRaw = (u.bhk || '').toString().toLowerCase();
          const unitNum = (unitBhkRaw.match(/(\d+)/) || [])[0];
          if (filterNum) {
            const fN = Number(filterNum);
            if (filterBhkRaw.includes('+')) {
              const uN = unitNum ? Number(unitNum) : NaN;
              return !isNaN(uN) && uN >= fN;
            }
            if (unitNum) return Number(unitNum) === fN;
            return unitBhkRaw.includes(filterNum);
          }
          const norm = (s) => (s || '').toString().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
          return norm(unitBhkRaw).includes(norm(filterBhkRaw));
        });
      }

      setMyUnits(normalized);
      setMyUnitsCount(total);

      // Fetch thumbnails for my units
      const unitIds = normalized.map(u => u._id || u.id).filter(Boolean);
      if (unitIds.length > 0) {
        inventoryAPI.getUnitThumbnails(unitIds)
          .then((thumbRes) => {
            const thumbs = thumbRes.data?.thumbnails || {};
            if (Object.keys(thumbs).length > 0) {
              setUnitThumbnails(prev => ({ ...prev, ...thumbs }));
            }
          })
          .catch(() => {});
      }
    } catch (err) {
      console.error('Failed to fetch my units:', err);
      setMyUnits([]);
    } finally {
      setMyUnitsLoading(false);
    }
  }, [filters]);

  // Fetch units
  const fetchUnits = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) return;
        // Normalize BHK filter: send digits or '5+' so backend matching is reliable
        if (key === 'bhk') {
          const raw = filters.bhk.toString();
          const digits = (raw.match(/(\d+)/) || [])[0];
          if (digits) {
            params.bhk = raw.includes('+') ? `${digits}+` : digits;
          } else {
            params.bhk = raw.replace(/\s+/g, '').toLowerCase();
          }
          return;
        }
        params[key] = filters[key];
      });
      // If the backend stores `bhk` in mixed string formats (e.g. '3bhk'),
      // some serverside filters may not match reliably. To ensure searches
      // always work, omit `bhk` when requesting from server and apply a
      // tolerant client-side filter afterwards.
      const bhkFilterForClient = params.bhk || null;
      if (bhkFilterForClient) delete params.bhk;

      // Fetch all units (no pagination limit) so the full inventory is displayed
      params.limit = 0;
      const res = await inventoryAPI.getUnits(params);

      // normalize unit fields (support snake_case and camelCase from backend)
      const raw = Array.isArray(res.data) ? res.data : (res.data?.units || res.data || []);
      const normalize = (u) => ({
        _id: u._id || u.id || u._id,
        id: u._id || u.id,
        unit_number: u.unit_number || u.unitNumber || u.name || '',
        name: u.name || u.unit_number || u.unitNumber || '',
        location: u.location || u.address || '',
        bhk: u.bhk || (u.bhk && String(u.bhk)) || '',
        status: u.status || u.state || 'available',
        built_up_area: u.built_up_area || u.builtUpArea || u.carpet_area || '',
        super_area: u.super_area || u.superArea || '',
        base_price: u.base_price || u.basePrice || u.price || 0,
        final_price: u.final_price || u.finalPrice || u.price || 0,
        price_per_sqft: u.price_per_sqft || u.pricePerSqft || u.price_per_sqft || 0,
        parking_slots: u.parking_slots || u.parking || u.parkingSlots || 0,
        listing_type: u.listing_type || u.listingType || u.looking_to || '',
        keys_location: u.keys_location || u.keysLocation || '',
        furnished_status: u.furnished_status || u.furnishedStatus || '',
        owner_name: u.owner_name || (u.ownerDetails && u.ownerDetails.name) || '',
        owner_phone: u.owner_phone || (u.ownerDetails && u.ownerDetails.phone) || '',
        availability_date: u.availability_date || u.availabilityDate || '',
        remarks: u.remarks || u.keys_remarks || '',
        // Thumbnail: prefer media array (image) or photos field from backend
        thumbnail: (() => {
          try {
            if (Array.isArray(u.media) && u.media.length > 0) return buildMediaUrl(u.media[0].url || u.media[0].filename || u.media[0]);
            if (Array.isArray(u.photos) && u.photos.length > 0) return buildMediaUrl(u.photos[0].url || u.photos[0].filename || u.photos[0].name || u.photos[0]);
            if (u.thumbnail) return buildMediaUrl(u.thumbnail);
            if (u.cover_image) return buildMediaUrl(u.cover_image);
          } catch (e) { /* ignore */ }
          return null;
        })(),
        building_name: u.building_name || u.buildingName || u.tower || '',
        project_name: (u.project && (u.project.name || u.project.title)) || u.project_name || (u.project && u.projectId) || '',
        tower: u.tower || '',
        amenities: (function(){
          try{
            if (Array.isArray(u.amenities)) return u.amenities.map(a => typeof a === 'string' ? a : (a.name || a.title || JSON.stringify(a)));
            if (typeof u.amenities === 'string') return u.amenities.split(',').map(s => s.trim()).filter(Boolean);
            if (Array.isArray(u.features)) return u.features.map(a => typeof a === 'string' ? a : (a.name || a.title || JSON.stringify(a)));
            if (Array.isArray(u.facilities)) return u.facilities.map(a => typeof a === 'string' ? a : (a.name || a.title || JSON.stringify(a)));
          }catch(e){}
          return [];
        })(),
        raw: u
      });

      let normalized = raw.map(normalize);

      // Apply tolerant client-side BHK filtering if requested
      if (bhkFilterForClient) {
        const filterBhkRaw = bhkFilterForClient.toString().toLowerCase();
        const filterNum = (filterBhkRaw.match(/(\d+)/) || [])[0];
        normalized = normalized.filter((u) => {
          const unitBhkRaw = (u.bhk || '').toString().toLowerCase();
          const unitNum = (unitBhkRaw.match(/(\d+)/) || [])[0];
          if (filterNum) {
            const fN = Number(filterNum);
            if (filterBhkRaw.includes('+')) {
              const uN = unitNum ? Number(unitNum) : NaN;
              return !isNaN(uN) && uN >= fN;
            }
            if (unitNum) return Number(unitNum) === fN;
            return unitBhkRaw.includes(filterNum);
          }
          // fallback to substring match (normalize spaces/underscores)
          const norm = (s) => (s || '').toString().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
          return norm(unitBhkRaw).includes(norm(filterBhkRaw));
        });
      }

      setUnits(normalized);

      // Fetch thumbnails from DO Spaces for all units (non-blocking)
      const unitIds = normalized.map(u => u._id || u.id).filter(Boolean);
      if (unitIds.length > 0) {
        inventoryAPI.getUnitThumbnails(unitIds)
          .then((thumbRes) => {
            const thumbs = thumbRes.data?.thumbnails || {};
            if (Object.keys(thumbs).length > 0) {
              setUnitThumbnails(prev => ({ ...prev, ...thumbs }));
            }
          })
          .catch((err) => {
            console.warn('Failed to fetch thumbnails:', err.message);
          });
      }

    } catch (err) {
      console.error('Failed to fetch units:', err);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProjects();
    fetchUnits();
    fetchStats();
    fetchMyOwnerRequests();
    fetchMyUnits();
  }, [fetchUnits, fetchMyUnits]);

  // Sync tab from URL search param (?tab=my)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'my' && activeTab !== 'my') setActiveTab('my');
  }, [searchParams]);

  // Handle tab switch
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === 'my') {
      searchParams.set('tab', 'my');
    } else {
      searchParams.delete('tab');
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Auto-open unit from URL query param (?unit=<id>)
  useEffect(() => {
    const unitParam = searchParams.get('unit');
    if (unitParam && !loading) {
      viewUnit(unitParam);
      // Clear the query param so it doesn't re-trigger
      searchParams.delete('unit');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Auto-refresh stats every 30 seconds for real-time accuracy
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnits();
      fetchMyUnits();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchUnits, fetchMyUnits]);

  // Real-time: listen for unit updates and inventory creation via shared socket
  const { on, off } = useSocket() || {};

  useEffect(() => {
    if (!on || !off) return;
    const handleUnitUpdated = (payload) => {
      try {
        const unitId = payload && (payload.unitId || (payload.unit && (payload.unit._id || payload.unit.id)));
        const updatedUnit = payload.unit || payload;
        if (!unitId) return;
        setUnits((prev) => prev.map((u) =>
          (u._id || u.id) === unitId ? { ...u, ...updatedUnit, raw: updatedUnit } : u
        ));
        setMyUnits((prev) => prev.map((u) =>
          (u._id || u.id) === unitId ? { ...u, ...updatedUnit, raw: updatedUnit } : u
        ));
        fetchStats();
      } catch (e) {
        console.error('Error applying unit-updated payload', e);
      }
    };
    on('unit-updated', handleUnitUpdated);
    return () => off('unit-updated', handleUnitUpdated);
  }, [on, off]);

  // Full refresh on inventory creation
  const refreshInventory = useCallback(() => {
    fetchUnits();
    fetchMyUnits();
    fetchStats();
    fetchProjects();
  }, [fetchUnits, fetchMyUnits]);
  useRealtimeData(['inventory-created'], refreshInventory);

  // Real-time: refresh "My Owner Requests" when access is approved/rejected
  useRealtimeData(['owner-access-approved', 'owner-access-updated', 'owner-access-rejected'], fetchMyOwnerRequests);

  // Real-time inventory logs (managers/admin)
  useEffect(() => {
    if (!on || !off || !isManager) return;
    const handleNewLog = (log) => {
      setInventoryLogs((prev) => [log, ...prev].slice(0, 100));
    };
    on('inventory-log', handleNewLog);
    return () => off('inventory-log', handleNewLog);
  }, [on, off, isManager]);

  // Helpers
  const getRequestUnitDisplay = (req) => {
    const unit = req.unitId;
    if (!unit || typeof unit === 'string') return 'Unit';
    const parts = [];
    if (unit.unitNumber) parts.push(`Unit ${unit.unitNumber}`);
    if (unit.bhk) parts.push(unit.bhk);
    if (unit.project?.name) parts.push(unit.project.name);
    if (unit.tower?.name) parts.push(unit.tower.name);
    if (unit.building_name) parts.push(unit.building_name);
    return parts.join(' - ') || 'Unit';
  };

  const buildMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${api.defaults.baseURL || ''}/${url}`.replace(/([^:]\/\/)\//g, '$1');
  };

  // Detect file type from key/name/extension (don't trust server type field)
  const detectMediaType = (item) => {
    const name = (item.name || item.key || item.originalname || '').toLowerCase();
    const ext = name.split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'tiff', 'tif', 'avif'].includes(ext)) return 'image';
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'video';
    if (item.contentType && item.contentType.startsWith('image/')) return 'image';
    if (item.contentType && item.contentType.startsWith('video/')) return 'video';
    if (item.mimetype && item.mimetype.startsWith('image/')) return 'image';
    if (item.mimetype && item.mimetype.startsWith('video/')) return 'video';
    return item.type || 'file';
  };

  // Get the best URL for a media item
  const getMediaSrc = (item) => {
    if (!item) return '';
    return item.downloadUrl || item.url || item.publicUrl || item.location || item.src || '';
  };

  // Normalize media items from any source (upload response, listMedia, etc.)
  const normalizeMediaItems = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map(item => ({
        ...item,
        type: detectMediaType(item),
        downloadUrl: getMediaSrc(item),
      }))
      .filter(m => m.downloadUrl || m.key); // keep items with URL or at least a key
  };

  const formatCurrency = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n) || isNaN(n)) return '-';
    return `₹${n.toLocaleString()}`;
  };

  const resolveListingKind = (u) => {
    const candidates = [
      u.listing_type,
      u.listingType,
      (u.raw && u.raw.listing_type),
      (u.raw && u.raw.listingType),
      u.looking_to,
      u.lookingTo,
      (u.raw && u.raw.looking_to),
      (u.raw && u.raw.lookingTo),
      u.transaction_type,
      (u.raw && u.raw.transaction_type),
    ];
    for (let c of candidates) {
      if (!c && typeof c !== 'number') continue;
      const s = String(c).toLowerCase();
      if (s.includes('rent')) return 'rent';
      if (s.includes('sale') || s.includes('sell')) return 'sale';
      if (s.includes('lease')) return 'rent';
    }
    return '';
  };

  const viewUnit = async (unitId) => {
    try {
      const res = await inventoryAPI.getUnit(unitId);
      const u = res.data || null;
      if (u) {
        const normalized = {
          _id: u._id || u.id,
          id: u._id || u.id,
          unit_number: u.unit_number || u.unitNumber || u.name || '',
          name: u.name || u.unit_number || u.unitNumber || '',
          location: u.location || u.address || '',
          bhk: u.bhk || '',
          status: u.status || 'available',
          built_up_area: u.built_up_area || u.builtUpArea || '',
          super_area: u.super_area || u.superArea || '',
          base_price: u.base_price || u.basePrice || 0,
          final_price: u.final_price || u.finalPrice || 0,
          price_per_sqft: u.price_per_sqft || u.pricePerSqft || 0,
          parking_slots: u.parking_slots || u.parking || 0,
          keys_location: u.keys_location || u.keysLocation || '',
          furnished_status: u.furnished_status || u.furnishedStatus || '',
          owner_name: u.owner_name || (u.ownerDetails && u.ownerDetails.name) || '',
          owner_phone: u.owner_phone || (u.ownerDetails && u.ownerDetails.phone) || '',
          availability_date: u.availability_date || u.availabilityDate || '',
          listing_type: u.listing_type || u.listingType || u.looking_to || '',
          remarks: u.remarks || u.keys_remarks || '',
          building_name: u.building_name || u.buildingName || u.tower || '',
          project_name: (u.project && (u.project.name || u.project.title)) || u.project_name || (u.project && u.projectId) || '',
          tower: u.tower || '',
          amenities: (function(){
            try{
              if (Array.isArray(u.amenities)) return u.amenities.map(a => typeof a === 'string' ? a : (a.name || a.title || JSON.stringify(a)));
              if (typeof u.amenities === 'string') return u.amenities.split(',').map(s => s.trim()).filter(Boolean);
              if (Array.isArray(u.features)) return u.features.map(a => typeof a === 'string' ? a : (a.name || a.title || JSON.stringify(a)));
              if (Array.isArray(u.facilities)) return u.facilities.map(a => typeof a === 'string' ? a : (a.name || a.title || JSON.stringify(a)));
            }catch(e){}
            return [];
          })(),
          tenant_name: u.tenant_name || u.raw?.tenant_name || u.tenantName || '',
          tenant_contact: u.tenant_contact || u.raw?.tenant_contact || u.tenantContact || '',
          tenant_start_date: u.tenant_start_date || u.raw?.tenant_start_date || u.tenantStartDate || '',
          tenant_end_date: u.tenant_end_date || u.raw?.tenant_end_date || u.tenantEndDate || '',
          thumbnail: u.thumbnail || u.cover_image || unitThumbnails[u._id || u.id] || null,
          raw: u
        };
        setSelectedUnit(normalized);
        setActiveMediaItem(null); // Reset active media when switching units
      } else {
        setSelectedUnit(null);
        setActiveMediaItem(null);
      }
      // Fetch media for this unit
      try {
        const mediaRes = await inventoryAPI.listUnitMedia(unitId);
        const mediaData = mediaRes?.data;
        const rawList = Array.isArray(mediaData) ? mediaData : (mediaData?.media || mediaData?.files || []);
        const mediaList = normalizeMediaItems(rawList);
        if (mediaList.length > 0) {
          setUnitMedia(mediaList);
        } else {
          setUnitMedia([]);
        }
      } catch (mediaErr) {
        console.warn('Failed to fetch unit media:', mediaErr.message);
      }
    } catch (err) {
      console.error('Failed to view unit:', err);
    }
  };

  // Re-fetch the currently selected unit (e.g. after owner access granted)
  const refreshUnitDetails = (unitId) => {
    if (unitId) viewUnit(unitId);
  };

  const handleUploadMedia = async (files) => {
    if (!selectedUnit) return;
    const unitId = selectedUnit.id || selectedUnit._id;
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const selectedFiles = Array.from(files || []);
      const totalFiles = selectedFiles.length;
      const totalBatches = Math.ceil(totalFiles / MEDIA_UPLOAD_BATCH_SIZE);
      const uploadedFiles = [];

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const start = batchIndex * MEDIA_UPLOAD_BATCH_SIZE;
        const end = start + MEDIA_UPLOAD_BATCH_SIZE;
        const batchFiles = selectedFiles.slice(start, end);

        const formData = new FormData();
        batchFiles.forEach((f) => formData.append('files', f));
        if (caption) formData.append('caption', caption);

        const res = await inventoryAPI.uploadUnitMedia(unitId, formData);
        const batchUploaded = res.data?.files || [];
        uploadedFiles.push(...batchUploaded);

        setUploadSuccess(`Uploading batch ${batchIndex + 1}/${totalBatches}...`);
      }

      const uploadedCount = uploadedFiles.length || totalFiles;
      setUploadSuccess(`${uploadedCount} file(s) uploaded successfully!`);
      setTimeout(() => setUploadSuccess(''), 5000);

      // Immediately show uploaded images from the upload response
      if (uploadedFiles.length > 0) {
        const newMedia = normalizeMediaItems(uploadedFiles);
        if (newMedia.length > 0) {
          setUnitMedia(prev => [...prev, ...newMedia]);
          const firstImg = newMedia.find(m => m.type === 'image' && m.downloadUrl);
          if (firstImg) {
            setUnitThumbnails(prev => ({ ...prev, [unitId]: firstImg.downloadUrl }));
          }
        }
      }

      // Refresh media from server (with small delay to allow DB to sync)
      setTimeout(async () => {
        try {
          const mediaRes = await inventoryAPI.listUnitMedia(unitId);
          const mediaData = mediaRes?.data;
          const rawList = Array.isArray(mediaData) ? mediaData : (mediaData?.media || mediaData?.files || []);
          const mediaList = normalizeMediaItems(rawList);
          if (mediaList.length > 0) {
            setUnitMedia(mediaList);
            const firstImg = mediaList.find(m => m.type === 'image' && m.downloadUrl);
            if (firstImg) {
              setUnitThumbnails(prev => ({ ...prev, [unitId]: firstImg.downloadUrl }));
            }
          }
        } catch (e) { /* silent */ }
      }, 1500);

      setCaption('');
    } catch (err) {
      console.error('Upload media failed:', err);
      const errorMsg = !err.response
        ? 'Network error — server may be starting up. Please wait and try again.'
        : (err.response?.data?.message || err.response?.data?.hint || err.message || 'Upload failed. Please try again.');
      setUploadError(errorMsg);
      setTimeout(() => setUploadError(''), 10000);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (mediaKey) => {
    if (!selectedUnit) return;
    try {
      await inventoryAPI.deleteUnitMedia(selectedUnit.id || selectedUnit._id, mediaKey);
      await viewUnit(selectedUnit.id || selectedUnit._id);
      // Refresh thumbnail for this unit card after deletion
      const unitId = selectedUnit.id || selectedUnit._id;
      inventoryAPI.getUnitThumbnails([unitId])
        .then((thumbRes) => {
          const thumbs = thumbRes.data?.thumbnails || {};
          // If no thumbnail left, clear it from state
          setUnitThumbnails(prev => {
            const updated = { ...prev };
            if (Object.keys(thumbs).length > 0) {
              Object.assign(updated, thumbs);
            } else {
              delete updated[unitId];
            }
            return updated;
          });
        })
        .catch(() => {});
    } catch (err) {
      console.error('Delete media failed:', err);
    }
  };

  const handleGeneratePDF = async () => {
    if (!selectedUnit) return;
    try {
      const res = await inventoryAPI.generatePDF(selectedUnit.id || selectedUnit._id);
      // inventoryAPI.generatePDF returns a blob (responseType: 'blob')
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const id = selectedUnit.id || selectedUnit._id || 'unit';
      a.download = `unit_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke URL after short delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Generate PDF failed:', err);
    }
  };

  const startEditUnit = async (unitId) => {
    try {
      setEditingUnitId(unitId);
      const res = await inventoryAPI.getUnit(unitId);
      setEditUnit(res.data || null);
    } catch (err) {
      console.error('Failed to start edit:', err);
    }
  };

  const handleEditUnit = async () => {
    if (!editingUnitId || !editUnit) return;
    setEditLoading(true);
    setEditError('');
    try {
      // Basic client-side validation for tenant info when status is booked or sold
      const status = (editUnit.status || '').toString().toLowerCase();
      if (status === 'booked' || status === 'sold') {
        const name = (editUnit.tenant_name || '').toString().trim();
        const contact = (editUnit.tenant_contact || '').toString().trim();
        const start = (editUnit.tenant_start_date || '').toString().trim();
        const end = (editUnit.tenant_end_date || '').toString().trim();
        if (!name) {
          setEditError('Tenant Name is required when unit is Booked or Sold');
          setEditLoading(false);
          return;
        }
        if (!contact) {
          setEditError('Tenant Contact Number is required when unit is Booked or Sold');
          setEditLoading(false);
          return;
        }
        if (!start) {
          setEditError('Tenant Start Date is required when unit is Booked or Sold');
          setEditLoading(false);
          return;
        }
        if (status === 'booked' && !end) {
          setEditError('Tenant End Date is required when unit status is Booked');
          setEditLoading(false);
          return;
        }
      }

      // Safe payload: include tenant fields if present
      const payload = { ...editUnit };
      // Send update
      await inventoryAPI.updateUnit(editingUnitId, payload);
      setEditingUnitId(null);
      setEditUnit(null);
      await fetchUnits();
      await fetchStats();
    } catch (err) {
      console.error('Edit unit failed:', err);
      setEditError(err.response?.data?.message || err.message || 'Failed to update');
    } finally {
      setEditLoading(false);
    }
  };

  const searchUnits = async () => {
    // uses filters state
    setHasSearched(true);
    await fetchUnits();
  };

  const resetFilters = () => {
    setFilters({
      query: '',
      project_id: '',
      status: '',
      bhk: '',
      listing_type: '',
      budget_min: '',
      budget_max: '',
      area_min: '',
      area_max: '',
      location: '',
      keys_location: '',
      facing: '',
      furnished_status: ''
    });
    setHasSearched(false);
    setUnits([]);
  };

  // Client-side displayed units (real-time, case-insensitive filtering)
  const sourceUnits = activeTab === 'my' ? myUnits : units;
  const displayedUnits = sourceUnits.filter((u) => {
    // start with true then apply filters
    try {
      const q = (filters.query || '').toString().trim().toLowerCase();

      if (q) {
        const fields = [
          (u.unit_number || u.name || '').toString().toLowerCase(),
          (u.owner_name || '').toString().toLowerCase(),
          (u.owner_phone || '').toString().toLowerCase(),
          (u.bhk || '').toString().toLowerCase(),
          ((u.raw && (u.raw.property_type || u.raw.propertyType)) || '').toString().toLowerCase()
        ];
        const matchesQuery = fields.some((f) => f && f.includes(q));
        if (!matchesQuery) return false;
      }

      if (filters.status && filters.status !== '' && (u.status || '').toString().toLowerCase() !== filters.status.toString().toLowerCase()) return false;
      if (filters.bhk && filters.bhk !== '') {
        const filterBhkRaw = filters.bhk.toString().toLowerCase();
        const unitBhkRaw = (u.bhk || '').toString().toLowerCase();
        const filterNum = (filterBhkRaw.match(/(\d+)/) || [])[0];
        const unitNum = (unitBhkRaw.match(/(\d+)/) || [])[0];

        if (filterNum) {
          const fN = Number(filterNum);
          if (filterBhkRaw.includes('+')) {
            // 5+ BHK means unitNum must be >=5
            const uN = unitNum ? Number(unitNum) : NaN;
            if (isNaN(uN) || uN < fN) return false;
          } else {
            // Exact numeric match when possible
            if (unitNum) {
              if (Number(unitNum) !== fN) return false;
            } else {
              // fallback: compare normalized strings
              if (!unitBhkRaw.includes(filterBhkRaw.replace(/\s+/g, ' ').trim()) && !unitBhkRaw.includes(filterNum)) return false;
            }
          }
        } else {
          // No number in filter — do a normalized substring match
          const norm = (s) => (s || '').toString().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
          if (!norm(unitBhkRaw).includes(norm(filterBhkRaw))) return false;
        }
      }
      if (filters.listing_type && filters.listing_type !== '' && ((u.listing_type || (u.raw && u.raw.listing_type)) || '').toString().toLowerCase() !== filters.listing_type.toString().toLowerCase()) return false;
      // Furnished matching: make 'furnished' match both 'furnished' and 'fully_furnished'
      const matchesFurnished = (filterVal, unitVal) => {
        const norm = (s) => (s || '').toString().toLowerCase().replace(/[_-]/g, ' ').trim();
        const f = norm(filterVal);
        const v = norm(unitVal);
        if (!f) return true;
        if (f === 'furnished') {
          return v === 'furnished' || v.includes('fully') || v === 'fully furnished' || v === 'fully_furnished';
        }
        if (f.includes('semi')) {
          return v.includes('semi');
        }
        if (f.includes('un')) {
          return v.includes('unfurn') || v === 'unfurnished';
        }
        return v === f;
      };

      if (filters.furnished_status && filters.furnished_status !== '' && !matchesFurnished(filters.furnished_status, u.furnished_status)) return false;
      if (filters.location && filters.location !== '' && !((u.location || '').toString().toLowerCase().includes(filters.location.toString().toLowerCase()))) return false;

      // numeric ranges
      const basePrice = Number(u.base_price || u.final_price || 0);
      if (filters.budget_min && Number(filters.budget_min) > 0 && basePrice < Number(filters.budget_min)) return false;
      if (filters.budget_max && Number(filters.budget_max) > 0 && basePrice > Number(filters.budget_max)) return false;

      const areaVal = Number(u.built_up_area || u.super_area || 0);
      if (filters.area_min && Number(filters.area_min) > 0 && areaVal < Number(filters.area_min)) return false;
      if (filters.area_max && Number(filters.area_max) > 0 && areaVal > Number(filters.area_max)) return false;

      return true;
    } catch (e) {
      return true;
    }
  });

  // Live stats computed from current units (either filtered or full list)
  const computeCounts = (arr) => {
    const res = {
      total_units: 0,
      for_sale: 0,
      for_rent: 0,
      available: 0,
      on_hold: 0,
      booked: 0,
      sold: 0,
      unspecified_listing: 0,
      unspecified_status: 0,
    };
    if (!Array.isArray(arr)) return res;
    res.total_units = arr.length;
    arr.forEach((u) => {
      const ltRaw = ((u.listing_type || (u.raw && u.raw.listing_type)) || '').toString();
      const lt = ltRaw.toLowerCase();
      let countedLt = false;
      if (lt.includes('sale') || lt.includes('sell') || lt === 'sell') { res.for_sale += 1; countedLt = true; }
      if (lt.includes('rent')) { res.for_rent += 1; countedLt = true; }
      if (!countedLt) res.unspecified_listing += 1;

      const stRaw = (u.status || u.state || '') || '';
      const st = stRaw.toString().toLowerCase();
      let countedSt = false;
      if (st.includes('available')) { res.available += 1; countedSt = true; }
      if (st.includes('hold') || st.includes('on hold')) { res.on_hold += 1; countedSt = true; }
      if (st.includes('book')) { res.booked += 1; countedSt = true; }
      if (st.includes('sold')) { res.sold += 1; countedSt = true; }
      if (!countedSt) res.unspecified_status += 1;
    });
    return res;
  };

  const liveStats = activeTab === 'my'
    ? computeCounts(hasSearched ? displayedUnits : myUnits)
    : computeCounts(hasSearched ? displayedUnits : units);

  // Render UI (kept the original structure / classes)
  return (
    <div className="flex h-screen w-full overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-100">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <div className={`flex-1 flex flex-col overflow-hidden ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        <Header user={user} />
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Inventory</h1>
            </div>
            <div className="flex items-center gap-3">
              {isManager && (
                <button
                  onClick={() => { setLogsModalOpen(true); fetchLogs(); }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-semibold flex items-center gap-2"
                >
                  <FiClock className="w-4 h-4" /> Activity Logs
                </button>
              )}
              <button
                onClick={() => setCreateModalOpen(true)}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-semibold"
                style={{ minWidth: '140px' }}
              >
                + Add Inventory
              </button>
            </div>
          </div>

          {/* Tab Bar — All Inventory / My Inventory */}
          <div className="flex items-center gap-1 mb-5 bg-white rounded-lg shadow p-1 w-fit">
            <button
              onClick={() => handleTabSwitch('all')}
              className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all ${
                activeTab === 'all'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              All Inventory
            </button>
            <button
              onClick={() => handleTabSwitch('my')}
              className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'my'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              My Inventory
              {myUnitsCount > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                  activeTab === 'my' ? 'bg-white text-emerald-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {myUnitsCount}
                </span>
              )}
            </button>
          </div>

          {/* Stats Cards — use liveStats (computed from loaded units) for accurate counts */}
          {(() => {
            const displayStats = liveStats || stats || {};
            const unspecified = Math.max(0, (displayStats.total_units || 0) - (displayStats.for_sale || 0) - (displayStats.for_rent || 0));
            const cards = [
              { label: 'Total Units', value: displayStats.total_units || 0, bg: 'bg-white', text: 'text-gray-900', sub: 'text-gray-500', border: 'border-gray-200', icon: '📊' },
              { label: 'For Sale', value: displayStats.for_sale || 0, bg: 'bg-blue-50', text: 'text-blue-700', sub: 'text-blue-500', border: 'border-blue-200', icon: '🏷️' },
              { label: 'For Rent', value: displayStats.for_rent || 0, bg: 'bg-orange-50', text: 'text-orange-700', sub: 'text-orange-500', border: 'border-orange-200', icon: '🔑' },
              { label: 'Unspecified', value: displayStats.unspecified_listing || unspecified, bg: 'bg-gray-50', text: 'text-gray-700', sub: 'text-gray-500', border: 'border-gray-200', icon: '❓' },
              { label: 'Available', value: displayStats.available || 0, bg: 'bg-green-50', text: 'text-green-700', sub: 'text-green-500', border: 'border-green-200', icon: '✅' },
              { label: 'On Hold', value: displayStats.on_hold || 0, bg: 'bg-yellow-50', text: 'text-yellow-700', sub: 'text-yellow-500', border: 'border-yellow-200', icon: '⏸️' },
              { label: 'Booked', value: displayStats.booked || 0, bg: 'bg-purple-50', text: 'text-purple-700', sub: 'text-purple-500', border: 'border-purple-200', icon: '📋' },
              { label: 'Sold', value: displayStats.sold || 0, bg: 'bg-red-50', text: 'text-red-700', sub: 'text-red-500', border: 'border-red-200', icon: '🏠' },
            ];
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3 mb-6">
                {cards.map((card, idx) => (
                  <div key={idx} className={`${card.bg} rounded-xl shadow-sm border ${card.border} p-4 hover:shadow-md transition-all duration-200 group`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-xs font-medium ${card.sub} uppercase tracking-wider`}>{card.label}</p>
                      <span className="text-sm opacity-60 group-hover:opacity-100 transition-opacity">{card.icon}</span>
                    </div>
                    <p className={`text-2xl font-extrabold ${card.text} tabular-nums`}>{card.value}</p>
                    <div className={`mt-2 h-0.5 rounded-full ${card.border} opacity-40`}></div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Search Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <h3 className="font-semibold mb-3">Search Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <select
                className="px-3 py-2 border rounded-md"
                value={filters.project_id}
                onChange={(e) => setFilters((p) => ({ ...p, project_id: e.target.value }))}
              >
                <option value="">All Projects</option>
                {projects && projects.length > 0 && (
                  projects.map((pr) => (
                    <option key={pr._id || pr.id} value={pr._id || pr.id}>{pr.name}</option>
                  ))
                )}
              </select>

              <select
                className="px-3 py-2 border rounded-md"
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="">All Status</option>
                <option value="available">Available</option>
                <option value="hold">On Hold</option>
                <option value="booked">Booked</option>
                <option value="sold">Sold</option>
              </select>

              <select
                className="px-3 py-2 border rounded-md"
                value={filters.bhk}
                onChange={(e) => setFilters((p) => ({ ...p, bhk: e.target.value }))}
              >
                <option value="">All BHK</option>
                <option value="1 BHK">1 BHK</option>
                <option value="2 BHK">2 BHK</option>
                <option value="3 BHK">3 BHK</option>
                <option value="4 BHK">4 BHK</option>
                <option value="5+ BHK">5+ BHK</option>
              </select>

              <select
                className="px-3 py-2 border rounded-md"
                value={filters.listing_type}
                onChange={(e) => setFilters((p) => ({ ...p, listing_type: e.target.value }))}
              >
                <option value="">All (Sale &amp; Rent)</option>
                <option value="sale">For Sale</option>
                <option value="rent">For Rent</option>
              </select>

              <input type="number" placeholder="Min Budget (₹)" className="px-3 py-2 border rounded-md" value={filters.budget_min || ''} onChange={(e) => setFilters((p) => ({ ...p, budget_min: e.target.value }))} />
              <input type="number" placeholder="Max Budget (₹)" className="px-3 py-2 border rounded-md" value={filters.budget_max || ''} onChange={(e) => setFilters((p) => ({ ...p, budget_max: e.target.value }))} />
              <input type="number" placeholder="Min Area (sq.ft)" className="px-3 py-2 border rounded-md" value={filters.area_min || ''} onChange={(e) => setFilters((p) => ({ ...p, area_min: e.target.value }))} />
              <input type="number" placeholder="Max Area (sq.ft)" className="px-3 py-2 border rounded-md" value={filters.area_max || ''} onChange={(e) => setFilters((p) => ({ ...p, area_max: e.target.value }))} />
              <input type="text" placeholder="Location" className="px-3 py-2 border rounded-md" value={filters.location || ''} onChange={(e) => setFilters((p) => ({ ...p, location: e.target.value }))} />
              <input type="text" placeholder="Search by unit, project, etc." className="px-3 py-2 border rounded-md" value={filters.query || ''} onChange={(e) => setFilters((p) => ({ ...p, query: e.target.value }))} />

              <select className="px-3 py-2 border rounded-md" value={filters.keys_location || ''} onChange={(e) => setFilters((p) => ({ ...p, keys_location: e.target.value }))}>
                <option value="">Keys Location</option>
                <option value="with_us">With Us</option>
                <option value="with_owner">With Owner</option>
                <option value="with_tenant">With Tenant</option>
                <option value="broker">With Broker</option>
              </select>

              <select className="px-3 py-2 border rounded-md" value={filters.facing || ''} onChange={(e) => setFilters((p) => ({ ...p, facing: e.target.value }))}>
                <option value="">All Facing</option>
                <option value="north">North</option>
                <option value="south">South</option>
                <option value="east">East</option>
                <option value="west">West</option>
                <option value="north-east">North-East</option>
                <option value="north-west">North-West</option>
                <option value="south-east">South-East</option>
                <option value="south-west">South-West</option>
              </select>

              <select className="px-3 py-2 border rounded-md" value={filters.furnished_status || ''} onChange={(e) => setFilters((p) => ({ ...p, furnished_status: e.target.value }))}>
                <option value="">Furnished Status</option>
                <option value="furnished">Furnished</option>
                <option value="semi-furnished">Semi-Furnished</option>
                <option value="unfurnished">Unfurnished</option>
              </select>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button onClick={searchUnits} className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-semibold">🔍 Search</button>
              <button onClick={resetFilters} className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">Reset</button>
            </div>
          </div>

          {/* My Owner Requests - Employees Only */}
          {!isManager && myOwnerRequests.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-6 border border-indigo-100">
              <div
                className="flex items-center justify-between px-5 py-3 cursor-pointer bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-lg"
                onClick={() => setMyRequestsExpanded(!myRequestsExpanded)}
              >
                <div className="flex items-center gap-2">
                  <FiShield className="text-indigo-600" size={18} />
                  <h3 className="font-bold text-indigo-800 text-sm">My Owner Access Requests</h3>
                  <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                    {myOwnerRequests.length}
                  </span>
                </div>
                <span className="text-gray-500 hover:text-gray-700 transition-transform duration-200"
                  style={{ transform: myRequestsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                  ▼
                </span>
              </div>

              {myRequestsExpanded && (
                <div className="p-4">
                  {myRequestsLoading ? (
                    <div className="text-center text-gray-500 py-4">Loading your requests...</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {myOwnerRequests.map((req) => {
                        const statusStyles = {
                          PENDING: { card: 'border-amber-200 bg-amber-50', badge: 'bg-amber-100 text-amber-700' },
                          APPROVED: { card: 'border-green-200 bg-green-50', badge: 'bg-green-100 text-green-700' },
                          REJECTED: { card: 'border-red-200 bg-red-50', badge: 'bg-red-100 text-red-700' },
                          EXPIRED: { card: 'border-gray-200 bg-gray-50', badge: 'bg-gray-100 text-gray-600' },
                        };
                        const style = statusStyles[req.status] || statusStyles.EXPIRED;
                        const unitId = req.unitId?._id || req.unitId;
                        const isClickable = unitId && typeof unitId === 'string';

                        return (
                          <div
                            key={req._id}
                            onClick={() => isClickable && viewUnit(unitId)}
                            className={`rounded-xl border-2 p-4 transition-all duration-200 ${style.card} ${isClickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <FiHome className="text-indigo-500 flex-shrink-0" size={16} />
                                <span className="font-semibold text-gray-800 text-sm truncate">
                                  {getRequestUnitDisplay(req)}
                                </span>
                              </div>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${style.badge}`}>
                                {req.status}
                              </span>
                            </div>

                            {req.reason && (
                              <p className="text-xs text-gray-500 mb-2 truncate" title={req.reason}>
                                Reason: {req.reason}
                              </p>
                            )}

                            <p className="text-xs text-gray-400 mb-2">
                              Requested: {new Date(req.createdAt).toLocaleDateString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric'
                              })}
                            </p>

                            {req.status === 'APPROVED' && req.expiresAt && (
                              <div className="mt-2 pt-2 border-t border-green-200">
                                {new Date(req.expiresAt) > new Date() ? (
                                  <div className="flex items-center gap-2">
                                    <FiClock className="text-green-500" size={13} />
                                    <CountdownTimer
                                      expiresAt={req.expiresAt}
                                      onExpire={() => fetchMyOwnerRequests()}
                                    />
                                  </div>
                                ) : (
                                  <span className="text-xs text-red-500 font-medium">Expired</span>
                                )}
                              </div>
                            )}

                            {req.status === 'APPROVED' && req.approvedByName && (
                              <p className="text-xs text-gray-400 mt-1">Approved by {req.approvedByName}</p>
                            )}

                            {req.status === 'REJECTED' && req.rejectionReason && (
                              <p className="text-xs text-red-500 mt-1">Reason: {req.rejectionReason}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Results */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {(activeTab === 'all' ? loading : myUnitsLoading) ? (
              <div className="p-8 text-center text-gray-500">
                {activeTab === 'my' ? 'Loading your inventory...' : 'Searching...'}
              </div>
            ) : activeTab === 'all' && !hasSearched ? (
              <div className="p-8 text-center text-gray-500">Use filters and click Search to view inventory units.</div>
            ) : displayedUnits.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {activeTab === 'my' ? 'You have not uploaded any inventory yet.' : 'No units found matching filters.'}
              </div>
            ) : (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayedUnits.map((u) => {
                    const unitId = u._id || u.id;
                    const thumb = u.thumbnail || unitThumbnails[unitId] || null;
                    return (
                      <InventoryCard
                        key={unitId}
                        data={thumb ? { ...u, thumbnail: thumb } : u}
                        user={user}
                        onView={() => viewUnit(unitId)}
                        onEdit={() => startEditUnit(unitId)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Unit Detail Modal */}
          {selectedUnit && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto">
              <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden shadow-2xl relative animate-fade-in">
                {/* Gradient Header Bar */}
                <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <FiHome className="text-white text-lg" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{selectedUnit.building_name || selectedUnit.project_name || 'Unit Details'}</h3>
                      <p className="text-blue-100 text-xs">{selectedUnit.bhk || 'Property'} {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && selectedUnit.unit_number ? `• Unit ${selectedUnit.unit_number}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const kind = resolveListingKind(selectedUnit || {});
                      if (kind === 'rent') return <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold tracking-wide">FOR RENT</span>;
                      if (kind === 'sale') return <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold tracking-wide">FOR SALE</span>;
                      const rawLabel = ((selectedUnit.raw && selectedUnit.raw.listing_type) || selectedUnit.listing_type || '').toString();
                      return rawLabel ? <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold tracking-wide">{rawLabel.toUpperCase()}</span> : null;
                    })()}
                    {selectedUnit.status && (() => {
                      const s = selectedUnit.status.toString().toLowerCase();
                      if (s.includes('sold')) return <span className="px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold">SOLD</span>;
                      if (s.includes('book')) return <span className="px-3 py-1 rounded-full bg-yellow-400 text-gray-900 text-xs font-bold">BOOKED</span>;
                      if (s.includes('hold')) return <span className="px-3 py-1 rounded-full bg-orange-400 text-white text-xs font-bold">ON HOLD</span>;
                      if (s.includes('available')) return <span className="px-3 py-1 rounded-full bg-emerald-400 text-white text-xs font-bold">AVAILABLE</span>;
                      return <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold">{selectedUnit.status.toUpperCase()}</span>;
                    })()}
                    <button onClick={() => { setSelectedUnit(null); setActiveMediaItem(null); }} className="ml-2 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
                      <FiX className="text-white text-lg" />
                    </button>
                  </div>
                </div>

                <div className="overflow-y-auto max-h-[calc(92vh-68px)]">
                  <div className="flex flex-col lg:flex-row">
                    {/* Left: Image Gallery (DigitalOcean Spaces) */}
                    <div className="lg:w-1/2 p-5">
                      <div className="rounded-xl overflow-hidden bg-gray-100 aspect-video shadow-inner relative">
                        {(() => {
                          // If user selected a specific media item, show it in the hero
                          const heroItem = activeMediaItem;
                          const heroType = heroItem ? detectMediaType(heroItem) : null;
                          const heroSrc = heroItem ? getMediaSrc(heroItem) : null;

                          if (heroItem && heroSrc) {
                            if (heroType === 'video') {
                              return (
                                <div className="relative w-full h-full">
                                  <video controls autoPlay className="w-full h-full object-contain bg-black">
                                    <source src={heroSrc} />
                                  </video>
                                  <button
                                    onClick={() => setVideoModalSrc(heroSrc)}
                                    className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-colors z-10"
                                    title="Full Screen"
                                  >
                                    <FiMaximize className="text-sm" />
                                  </button>
                                </div>
                              );
                            }
                            return <img src={heroSrc} alt={heroItem.name || ''} className="w-full h-full object-cover" onError={(e) => { e.target.onerror=null; e.target.style.display='none'; }} />;
                          }

                          // Default: find first displayable media item
                          const firstImage = unitMedia?.find(m => detectMediaType(m) === 'image' && getMediaSrc(m));
                          const firstVideo = unitMedia?.find(m => detectMediaType(m) === 'video' && getMediaSrc(m));
                          const anyMedia = unitMedia?.find(m => getMediaSrc(m));
                          const imgSrc = firstImage ? getMediaSrc(firstImage) : null;
                          const vidSrc = firstVideo ? getMediaSrc(firstVideo) : null;
                          const anySrc = anyMedia ? getMediaSrc(anyMedia) : null;
                          const thumbSrc = selectedUnit?.thumbnail || unitThumbnails[selectedUnit?._id || selectedUnit?.id];

                          if (imgSrc) {
                            return <img src={imgSrc} alt={firstImage.name || ''} className="w-full h-full object-cover" onError={(e) => { e.target.onerror=null; e.target.style.display='none'; }} />;
                          } else if (vidSrc) {
                            return (
                              <div className="relative w-full h-full">
                                <video controls className="w-full h-full object-contain bg-black">
                                  <source src={vidSrc} />
                                </video>
                                <button
                                  onClick={() => setVideoModalSrc(vidSrc)}
                                  className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-colors z-10"
                                  title="Full Screen"
                                >
                                  <FiMaximize className="text-sm" />
                                </button>
                              </div>
                            );
                          } else if (anySrc) {
                            return <img src={anySrc} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.onerror=null; e.target.style.display='none'; }} />;
                          } else if (thumbSrc) {
                            return <img src={thumbSrc} alt={selectedUnit?.name || ''} className="w-full h-full object-cover" onError={(e) => { e.target.onerror=null; e.target.style.display='none'; }} />;
                          } else {
                            return (
                              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                <FiHome className="text-4xl mb-2" />
                                <span className="text-sm">No image available</span>
                                <button
                                  onClick={async () => {
                                    const unitId = selectedUnit?.id || selectedUnit?._id;
                                    if (!unitId) return;
                                    try {
                                      const mediaRes = await inventoryAPI.listUnitMedia(unitId);
                                      const mediaData = mediaRes?.data;
                                      const rawList = Array.isArray(mediaData) ? mediaData : (mediaData?.media || mediaData?.files || []);
                                      const mediaList = normalizeMediaItems(rawList);
                                      if (mediaList.length > 0) {
                                        setUnitMedia(mediaList);
                                      }
                                    } catch (e) {
                                      console.error('Refresh media failed:', e);
                                    }
                                  }}
                                  className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                >
                                  Refresh Media
                                </button>
                              </div>
                            );
                          }
                        })()}
                      </div>
                      {/* Thumbnail Grid with Download */}
                      {unitMedia.length > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Media ({unitMedia.length} files)</span>
                            <button
                              onClick={async () => {
                                for (const m of unitMedia) {
                                  try {
                                    const dlUrl = getMediaSrc(m);
                                    if (!dlUrl) continue;
                                    const response = await fetch(dlUrl);
                                    const blob = await response.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = m.name || `file_${Date.now()}`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(url);
                                    await new Promise(r => setTimeout(r, 500));
                                  } catch (err) {
                                    console.error('Download failed for:', m.name, err);
                                  }
                                }
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-all"
                            >
                              <FiDownload className="text-xs" /> Download All
                            </button>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            {unitMedia.map((m, idx) => {
                              const mediaSrc = getMediaSrc(m);
                              const mediaType = detectMediaType(m);
                              const isActive = activeMediaItem && (activeMediaItem.key || activeMediaItem.name) === (m.key || m.name);
                              return (
                              <div
                                key={m.key || idx}
                                className={`relative group aspect-square overflow-hidden rounded-lg border-2 cursor-pointer transition-all shadow-sm ${
                                  isActive ? 'border-blue-500 ring-2 ring-blue-300' : 'border-transparent hover:border-blue-400'
                                }`}
                                onClick={() => setActiveMediaItem(m)}
                              >
                                {mediaType === 'image' && mediaSrc ? (
                                  <img src={mediaSrc} alt={m.name || ''} className="w-full h-full object-cover" onError={(e) => { e.target.onerror=null; e.target.style.display='none'; }} />
                                ) : mediaType === 'video' && mediaSrc ? (
                                  <div className="relative w-full h-full">
                                    <video className="w-full h-full object-cover" muted>
                                      <source src={mediaSrc} />
                                    </video>
                                    {/* Play button overlay for video */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="w-10 h-10 bg-black/60 rounded-full flex items-center justify-center shadow-lg">
                                        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20"><polygon points="6,4 16,10 6,16" /></svg>
                                      </div>
                                    </div>
                                    {/* VIDEO badge */}
                                    <div className="absolute top-1 left-1 bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                                      Video
                                    </div>
                                  </div>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-500 text-xs">
                                    <FiFileText className="text-2xl" />
                                  </div>
                                )}
                                {/* Hover overlay with actions */}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                  {mediaType === 'video' && mediaSrc && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setVideoModalSrc(mediaSrc); }}
                                      className="p-1.5 bg-white rounded-full shadow hover:bg-green-100 transition-colors"
                                      title="Play Video"
                                    >
                                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20"><polygon points="6,4 16,10 6,16" /></svg>
                                    </button>
                                  )}
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const dlUrl = getMediaSrc(m);
                                        if (!dlUrl) return;
                                        const response = await fetch(dlUrl);
                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = m.name || `file_${Date.now()}`;
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        window.URL.revokeObjectURL(url);
                                      } catch (err) {
                                        console.error('Download failed:', err);
                                      }
                                    }}
                                    className="p-1.5 bg-white rounded-full shadow hover:bg-blue-100 transition-colors"
                                    title="Download"
                                  >
                                    <FiDownload className="text-blue-600 text-sm" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteMedia(m.key); }}
                                    className="p-1.5 bg-white rounded-full shadow hover:bg-red-100 transition-colors"
                                    title="Delete"
                                  >
                                    <FiX className="text-red-600 text-sm" />
                                  </button>
                                </div>
                                {/* File size badge */}
                                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded">
                                  {m.size ? (m.size > 1048576 ? `${(m.size / 1048576).toFixed(1)} MB` : `${(m.size / 1024).toFixed(0)} KB`) : ''}
                                </div>
                              </div>
                            ); })}
                          </div>
                        </div>
                      )}

                      {/* Upload Section */}
                      <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
                        <label className="flex flex-col items-center justify-center cursor-pointer py-3">
                          <input
                            type="file"
                            multiple
                            accept="image/*,video/*,.pdf,.heic,.heif"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                handleUploadMedia(e.target.files);
                                e.target.value = '';
                              }
                            }}
                            disabled={uploading}
                          />
                          <FiDownload className="text-blue-500 text-xl mb-1 rotate-180" />
                          <span className="text-xs text-blue-700 font-semibold">{uploading ? 'Uploading...' : 'Click to upload photos/videos'}</span>
                          <span className="text-[10px] text-blue-400 mt-0.5">Files are stored on DigitalOcean Spaces (large selections auto-upload in batches)</span>
                        </label>
                        {uploadError && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-xs text-red-600 font-medium">Upload Failed: {uploadError}</p>
                          </div>
                        )}
                        {uploadSuccess && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-xs text-green-600 font-medium">{uploadSuccess}</p>
                          </div>
                        )}
                      </div>

                      {/* Price Card */}
                      <div className="mt-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
                        <div className="flex items-center gap-2 text-emerald-700 mb-1">
                          <FiDollarSign className="text-lg" />
                          <span className="text-sm font-semibold">Price</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(selectedUnit.base_price || selectedUnit.final_price)}</div>
                        {selectedUnit.price_per_sqft && (
                          <div className="text-xs text-gray-500 mt-1">₹{selectedUnit.price_per_sqft}/sqft</div>
                        )}
                      </div>
                    </div>

                    {/* Right: Details */}
                    <div className="lg:w-1/2 p-5 lg:border-l border-gray-100">
                      {/* Property Info Grid */}
                      <div className="mb-5">
                        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <FiFileText className="text-blue-500" /> Property Information
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          {(selectedUnit.property_type === 'commercial' ? [
                            { icon: <FiHome className="text-blue-500" />, label: 'Property Type', value: 'Commercial' },
                            { icon: <FiBriefcase className="text-purple-500" />, label: 'Config Type', value: selectedUnit.config_type },
                            { icon: <FiMapPin className="text-red-500" />, label: 'Building', value: selectedUnit.building_name || selectedUnit.project_name },
                            { icon: <FiTag className="text-indigo-500" />, label: 'Looking To', value: selectedUnit.looking_to },
                            { icon: <FiMapPin className="text-pink-500" />, label: 'City', value: selectedUnit.city },
                            { icon: <FiNavigation className="text-rose-500" />, label: 'Locality', value: selectedUnit.locality },
                            { icon: <FiMapPin className="text-gray-500" />, label: 'Address', value: [selectedUnit.address_line1, selectedUnit.address_line2, selectedUnit.landmark].filter(Boolean).join(', ') || null },
                            { icon: <FiMapPin className="text-blue-400" />, label: 'Pincode', value: selectedUnit.pincode },
                            { icon: <FiMapPin className="text-indigo-400" />, label: 'State', value: selectedUnit.state },
                            { icon: <FiActivity className="text-amber-500" />, label: 'Possession Status', value: selectedUnit.possession_status?.replace(/_/g, ' ') },
                            { icon: <FiGrid className="text-teal-500" />, label: 'Zone Type', value: selectedUnit.zone_type?.replace(/_/g, ' ') },
                            { icon: <FiBriefcase className="text-blue-600" />, label: 'Location Hub', value: selectedUnit.location_hub?.replace(/_/g, ' ') },
                            { icon: <FiBox className="text-green-500" />, label: 'Property Condition', value: selectedUnit.property_condition?.replace(/_/g, ' ') },
                            { icon: <FiCalendar className="text-orange-400" />, label: 'Property Age', value: selectedUnit.age != null && selectedUnit.age !== '' ? `${selectedUnit.age} years` : null },
                            { icon: <FiMaximize className="text-cyan-500" />, label: 'Built-up Area', value: selectedUnit.built_up_area ? `${selectedUnit.built_up_area} sqft` : null },
                            { icon: <FiMaximize className="text-teal-400" />, label: 'Carpet Area', value: selectedUnit.carpet_area ? `${selectedUnit.carpet_area} sqft` : null },
                            { icon: <FiMaximize className="text-indigo-500" />, label: 'Super Area', value: selectedUnit.super_area ? `${selectedUnit.super_area} sqft` : null },
                            { icon: <FiShield className="text-violet-500" />, label: 'Ownership', value: selectedUnit.ownership?.replace(/_/g, ' ') },
                            { icon: <FiGrid className="text-green-500" />, label: 'Furnished Status', value: selectedUnit.furnished_status?.replace(/_/g, ' ') },
                            { icon: <FiDollarSign className="text-emerald-500" />, label: 'Price', value: selectedUnit.base_price ? `₹${Number(selectedUnit.base_price).toLocaleString('en-IN')}` : null },
                            { icon: <FiDollarSign className="text-green-600" />, label: 'Final Price', value: selectedUnit.final_price ? `₹${Number(selectedUnit.final_price).toLocaleString('en-IN')}` : null },
                            { icon: <FiDollarSign className="text-teal-600" />, label: 'Price/sqft', value: selectedUnit.price_per_sqft ? `₹${Number(selectedUnit.price_per_sqft).toLocaleString('en-IN')}` : null },
                            { icon: <FiTag className="text-green-600" />, label: 'Negotiable', value: selectedUnit.negotiable },
                            { icon: <FiClipboard className="text-gray-500" />, label: 'Tax & Govt. Included', value: selectedUnit.tax_govt_included },
                            { icon: <FiActivity className="text-orange-500" />, label: 'DG & UPS Included', value: selectedUnit.dg_ups_included },
                            { icon: <FiDollarSign className="text-amber-500" />, label: 'Maintenance', value: selectedUnit.maintenance_charges?.replace(/_/g, ' ') },
                            { icon: <FiLayers className="text-purple-500" />, label: 'Floor', value: selectedUnit.your_floor ? `${selectedUnit.your_floor}${selectedUnit.total_floors ? ` / ${selectedUnit.total_floors}` : ''}` : (selectedUnit.floor_number ? `${selectedUnit.floor_number}${selectedUnit.total_floors ? ` / ${selectedUnit.total_floors}` : ''}` : null) },
                            { icon: <FiLayers className="text-blue-400" />, label: 'Staircases', value: selectedUnit.number_of_staircase != null && selectedUnit.number_of_staircase !== 0 ? selectedUnit.number_of_staircase : null },
                            { icon: <FiNavigation className="text-indigo-500" />, label: 'Passenger Lifts', value: selectedUnit.passenger_lifts != null && selectedUnit.passenger_lifts !== 0 ? selectedUnit.passenger_lifts : null },
                            { icon: <FiPackage className="text-gray-600" />, label: 'Service Lifts', value: selectedUnit.service_lifts != null && selectedUnit.service_lifts !== 0 ? selectedUnit.service_lifts : null },
                            { icon: <FiTruck className="text-blue-500" />, label: 'Private Parking', value: selectedUnit.private_parking != null && selectedUnit.private_parking !== 0 ? selectedUnit.private_parking : null },
                            { icon: <FiTruck className="text-gray-500" />, label: 'Public Parking', value: selectedUnit.public_parking != null && selectedUnit.public_parking !== 0 ? selectedUnit.public_parking : null },
                            { icon: <FiSun className="text-yellow-500" />, label: 'Facing', value: selectedUnit.facing },
                            { icon: <FiAward className="text-amber-600" />, label: 'Pre-leased/Pre-rented', value: selectedUnit.is_pre_leased },
                            { icon: <FiCalendar className="text-orange-500" />, label: 'Available From', value: selectedUnit.availability_date ? new Date(selectedUnit.availability_date).toLocaleDateString() : null },
                            { icon: <FiKey className="text-gray-500" />, label: 'Keys Location', value: selectedUnit.keys_location || selectedUnit.keysLocation },
                            { icon: <FiClipboard className="text-gray-400" />, label: 'Keys Remarks', value: selectedUnit.keys_remarks },
                          ].map(item => ({ ...item, value: item.value || '—' })) : [
                            { icon: <FiHome className="text-blue-500" />, label: 'Property Type', value: selectedUnit.bhk },
                            { icon: <FiLayers className="text-purple-500" />, label: 'Floor', value: selectedUnit.floor_number ? `${selectedUnit.floor_number}${selectedUnit.total_floors ? ` / ${selectedUnit.total_floors}` : ''}` : null },
                            { icon: <FiMapPin className="text-red-500" />, label: 'Building', value: selectedUnit.building_name || selectedUnit.project_name },
                            { icon: <FiMaximize className="text-teal-500" />, label: 'Area', value: selectedUnit.area || selectedUnit.carpet_area ? `${selectedUnit.area || selectedUnit.carpet_area} sqft` : null },
                            { icon: <FiMaximize className="text-indigo-500" />, label: 'Super Area', value: selectedUnit.super_area ? `${selectedUnit.super_area} sqft` : null },
                            { icon: <FiMaximize className="text-cyan-500" />, label: 'Built-up Area', value: selectedUnit.built_up_area ? `${selectedUnit.built_up_area} sqft` : null },
                            { icon: <FiSun className="text-yellow-500" />, label: 'Facing', value: selectedUnit.facing },
                            { icon: <FiGrid className="text-green-500" />, label: 'Furnished', value: selectedUnit.furnished_status },
                            { icon: <FiTruck className="text-gray-500" />, label: 'Parking', value: selectedUnit.parking_slots },
                            { icon: <FiCalendar className="text-orange-500" />, label: 'Available', value: selectedUnit.availability_date ? new Date(selectedUnit.availability_date).toLocaleDateString() : null },
                            { icon: <FiDollarSign className="text-emerald-500" />, label: 'Maintenance', value: selectedUnit.maintenance_charges?.replace(/_/g, ' ') },
                          ]).filter(item => item.value).map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                              <div className="mt-0.5">{item.icon}</div>
                              <div>
                                <div className="text-[11px] text-gray-400 uppercase font-semibold tracking-wide">{item.label}</div>
                                <div className={`text-sm font-medium capitalize ${item.value === '—' ? 'text-gray-400 italic' : 'text-gray-800'}`}>{item.value}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Owner Information - Access controlled */}
                      <OwnerAccessSection
                        unit={selectedUnit}
                        user={user}
                        onAccessGranted={() => refreshUnitDetails(selectedUnit._id || selectedUnit.id)}
                      />

                      {/* Remarks */}
                      {selectedUnit.remarks && (
                        <div className="mb-5">
                          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <FiMessageSquare className="text-blue-500" /> Remarks
                          </h4>
                          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 leading-relaxed">{selectedUnit.remarks}</p>
                        </div>
                      )}

                      {/* Amenities (Residential) */}
                      {selectedUnit.amenities && selectedUnit.amenities.length > 0 && (
                        <div className="mb-5">
                          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FiCheckCircle className="text-blue-500" /> Amenities
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedUnit.amenities.map((a, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full border border-blue-100">
                                <FiCheckCircle className="text-blue-400 text-[10px]" /> {a}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Commercial Amenities */}
                      {selectedUnit.commercial_amenities && selectedUnit.commercial_amenities.length > 0 && (
                        <div className="mb-5">
                          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FiCheckCircle className="text-purple-500" /> Commercial Amenities
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedUnit.commercial_amenities.map((a, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 bg-gradient-to-r from-purple-50 to-violet-50 text-purple-700 text-xs font-medium px-3 py-1.5 rounded-full border border-purple-100 capitalize">
                                <FiCheckCircle className="text-purple-400 text-[10px]" /> {a.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tenant Information */}
                      {(selectedUnit.tenant_name || selectedUnit.tenant_contact) && (
                        <div className="mb-5">
                          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FiKey className="text-blue-500" /> Tenant Information
                          </h4>
                          <div className="bg-amber-50 rounded-xl p-4 space-y-2 border border-amber-100">
                            {selectedUnit.tenant_name && (
                              <div className="flex items-center gap-2 text-sm">
                                <FiUser className="text-amber-600" />
                                <span className="text-gray-800 font-medium">{selectedUnit.tenant_name}</span>
                              </div>
                            )}
                            {selectedUnit.tenant_contact && (
                              <div className="flex items-center gap-2 text-sm">
                                <FiPhone className="text-amber-600" />
                                <span className="text-gray-800">{selectedUnit.tenant_contact}</span>
                              </div>
                            )}
                            {(selectedUnit.tenant_start_date || selectedUnit.tenant_end_date) && (
                              <div className="flex items-center gap-2 text-sm">
                                <FiCalendar className="text-amber-600" />
                                <span className="text-gray-800">
                                  {selectedUnit.tenant_start_date ? new Date(selectedUnit.tenant_start_date).toLocaleDateString() : '—'}
                                  {' → '}
                                  {selectedUnit.tenant_end_date ? new Date(selectedUnit.tenant_end_date).toLocaleDateString() : '—'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons Footer */}
                  <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center justify-center gap-3">
                    <button onClick={() => viewUnit(selectedUnit.id || selectedUnit._id)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-lg shadow-blue-200 transition-all hover:shadow-xl hover:-translate-y-0.5">
                      <FiEye /> View
                    </button>
                    <button onClick={() => startEditUnit(selectedUnit.id || selectedUnit._id)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold shadow-lg shadow-emerald-200 transition-all hover:shadow-xl hover:-translate-y-0.5">
                      <FiEdit2 /> Edit
                    </button>
                    <button onClick={handleGeneratePDF} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-xl text-sm font-semibold shadow-lg shadow-red-200 transition-all hover:shadow-xl hover:-translate-y-0.5">
                      <FiDownload /> Generate PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fixed footer action buttons for selected unit removed — actions are inside details column now */}

          {/* Create Inventory Modal */}
          {createModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
              <div className="bg-white rounded-lg p-0 w-full max-w-2xl mx-4 sm:mx-0 max-h-[95vh] overflow-y-auto">
                <div className="p-0">
                  <AddInventoryForm
                    loading={createLoading}
                    onSubmit={async (form) => {
                      setCreateLoading(true);
                      setCreateError('');
                      try {
                        // Create project/tower/unit as the original code intended
                        let projectId;
                        const projectName = form.buildingName || form.commercialBuilding || form.city || 'Default Project';
                        const existingProjects = await inventoryAPI.getProjects();
                        const existingProject = (existingProjects.data || []).find((p) => p.name === projectName);

                        if (existingProject) {
                          projectId = existingProject._id;
                        } else {
                          const newProject = await inventoryAPI.createProject({
                            name: projectName,
                            location: form.city,
                            description: `${form.propertyType} - ${form.lookingTo}`
                          });
                          projectId = newProject.data._id;
                        }

                        // towers
                        let towerId;
                        const towerName = form.buildingName || form.commercialBuilding || 'Main Tower';
                        const existingTowers = await inventoryAPI.getTowers(projectId);
                        const existingTower = (existingTowers.data || []).find((t) => t.name === towerName);
                        if (existingTower) {
                          towerId = existingTower._id;
                        } else {
                          const newTower = await inventoryAPI.createTower(projectId, { name: towerName, description: form.configType });
                          towerId = newTower.data._id;
                        }

                        const payload = {
                          project: projectId,
                          tower: towerId,
                          unitNumber: form.unitNumber || form.configType + '-' + Date.now(),
                          property_type: form.propertyType,
                          looking_to: form.lookingTo,
                          city: form.city,
                          building_name: form.buildingName || form.commercialBuilding || '',
                          config_type: form.configType,
                          bhk: form.bhk,
                          built_up_area: Number(form.builtUpArea || form.commercialBuiltUpArea) || 0,
                          super_area: Number(form.superArea) || 0,
                          floor_number: Number(form.floorNumber) || 0,
                          total_floors: Number(form.totalFloors || form.commercialTotalFloors) || 0,
                          age: Number(form.age) || 0,
                          bathrooms: Number(form.bathrooms) || 0,
                          balconies: Number(form.balconies) || 0,
                          parking_slots: Number(form.parking) || 0,
                          facing: form.facing,
                          furnished_status: form.furnishType,
                          maintenance_charges: form.maintenanceCharges || '',
                          amenities: form.amenities || [],
                          keys_location: form.keysLocation,
                          keys_remarks: form.keysRemarks,
                          availability_date: form.availabilityDate,
                          owner_name: form.ownerDetails?.name || '',
                          owner_phone: form.ownerDetails?.phone || '',
                          owner_email: form.ownerDetails?.email || '',
                          location: `${form.addressLine1 || ''}, ${form.addressLine2 || ''}, ${form.city}, ${form.state || ''}`,
                          address_line1: form.addressLine1,
                          address_line2: form.addressLine2,
                          pincode: form.pincode,
                          landmark: form.landmark,
                          state: form.state,
                          base_price: Number(form.basePrice || form.commercialPrice) || 0,
                          final_price: Number(form.finalPrice || form.commercialPrice) || 0,
                          price_per_sqft: Number(form.pricePerSqft) || 0,
                          // Commercial-specific fields
                          locality: form.commercialLocality || '',
                          possession_status: form.possessionStatus || '',
                          zone_type: form.zoneType || '',
                          location_hub: form.locationHub || '',
                          property_condition: form.propertyCondition || '',
                          ownership: form.ownership || '',
                          negotiable: form.negotiable || '',
                          tax_govt_included: form.taxGovtIncluded || '',
                          dg_ups_included: form.dgUpsIncluded || '',
                          your_floor: form.yourFloor || '',
                          number_of_staircase: Number(form.numberOfStaircase) || 0,
                          passenger_lifts: Number(form.passengerLifts) || 0,
                          service_lifts: Number(form.serviceLifts) || 0,
                          private_parking: Number(form.privateParking) || 0,
                          public_parking: Number(form.publicParking) || 0,
                          is_pre_leased: form.isPreLeased || '',
                          commercial_amenities: form.commercialAmenities || [],
                          status: 'available'

                        };

                        // Create unit with JSON payload
                        const res = await inventoryAPI.createUnit(payload);
                        const unitId = res.data?._id || res.data?.id || res.data?.id;

                        // If photos were provided, upload them separately to the media endpoint
                        let mediaUploadFailed = false;
                        if (unitId && form.photos && form.photos.length > 0) {
                          try {
                            const selectedFiles = Array.from(form.photos || []);
                            const totalBatches = Math.ceil(selectedFiles.length / MEDIA_UPLOAD_BATCH_SIZE);

                            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                              const start = batchIndex * MEDIA_UPLOAD_BATCH_SIZE;
                              const end = start + MEDIA_UPLOAD_BATCH_SIZE;
                              const batchFiles = selectedFiles.slice(start, end);
                              const mediaForm = new FormData();
                              batchFiles.forEach((file) => mediaForm.append('files', file));
                              await inventoryAPI.uploadUnitMedia(unitId, mediaForm);
                            }
                          } catch (mediaErr) {
                            console.error('Failed to upload photos:', mediaErr);
                            mediaUploadFailed = true;
                            setCreateError('Unit created but media upload failed: ' + (mediaErr.response?.data?.message || mediaErr.message || 'Check backend logs'));
                          }
                        }

                        if (!mediaUploadFailed) {
                          setCreateModalOpen(false);
                        }
                        // NOTE: Do NOT call fetchUnits() here — the backend emits an
                        // 'inventory-created' socket event which triggers refreshInventory
                        // (via useRealtimeData) and already refetches units, stats & projects.
                        // Calling fetchUnits() here as well caused the new item to appear
                        // twice: once from this explicit fetch, and once from the socket-
                        // triggered fetch.
                        if (unitId) await viewUnit(unitId);
                      } catch (err) {
                        console.error('Create unit error:', err);
                        setCreateError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create unit');
                      } finally {
                        setCreateLoading(false);
                      }
                    }}
                  />
                  {createError && <div className="text-red-500 text-xs p-2">{createError}</div>}
                  {createLoading && <div className="text-gray-500 text-xs p-2">Saving...</div>}
                </div>
              </div>
            </div>
          )}

          {/* Edit Unit Modal */}
          {editingUnitId && editUnit && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
              <div className="bg-white rounded-lg p-5 w-full max-w-3xl mx-4 sm:mx-0 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-bold">Edit Unit {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && editUnit.unit_number ? `(Unit: ${editUnit.unit_number})` : ''}</h2>
                  <button
                    onClick={() => {
                      setEditingUnitId(null);
                      setEditUnit(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ✕
                  </button>
                </div>

                {editError && (
                  <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{editError}</div>
                )}

                {editUnit.updated_at && editUnit.edited_by_first_name && (
                  <div className="mb-4 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                    <strong>Last edited:</strong> {editUnit.edited_by_first_name} {editUnit.edited_by_last_name} on {new Date(editUnit.updated_at).toLocaleDateString()} at {new Date(editUnit.updated_at).toLocaleTimeString()}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Property Details Section (kept controls similar to original) */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3">Property Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Floor Number</label>
                        <input type="number" value={editUnit.floor_number || ''} onChange={(e) => setEditUnit((p) => ({ ...p, floor_number: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">BHK</label>
                        <select value={editUnit.bhk || ''} onChange={(e) => setEditUnit((p) => ({ ...p, bhk: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                          <option value="">Select</option>
                          <option value="1 BHK">1 BHK</option>
                          <option value="2 BHK">2 BHK</option>
                          <option value="3 BHK">3 BHK</option>
                          <option value="4 BHK">4 BHK</option>
                          <option value="5+ BHK">5+ BHK</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Property Type</label>
                        <select value={editUnit.listing_type || 'sale'} onChange={(e) => setEditUnit((p) => ({ ...p, listing_type: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                          <option value="sale">For Sale</option>
                          <option value="rent">For Rent</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Carpet Area (sq.ft)</label>
                        <input type="number" value={editUnit.carpet_area || ''} onChange={(e) => setEditUnit((p) => ({ ...p, carpet_area: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Built-up Area (sq.ft)</label>
                        <input type="number" value={editUnit.built_up_area || ''} onChange={(e) => setEditUnit((p) => ({ ...p, built_up_area: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Facing</label>
                        <select value={editUnit.facing || ''} onChange={(e) => setEditUnit((p) => ({ ...p, facing: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                          <option value="">Select facing</option>
                          <option value="north">North</option>
                          <option value="south">South</option>
                          <option value="east">East</option>
                          <option value="west">West</option>
                          <option value="northeast">Northeast</option>
                          <option value="northwest">Northwest</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Furnished Status</label>
                        <select value={editUnit.furnished_status || ''} onChange={(e) => setEditUnit((p) => ({ ...p, furnished_status: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                          <option value="">Select status</option>
                          <option value="unfurnished">Unfurnished</option>
                          <option value="semi_furnished">Semi Furnished</option>
                          <option value="fully_furnished">Fully Furnished</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Parking Slots</label>
                        <input type="number" value={editUnit.parking_slots || 0} onChange={(e) => setEditUnit((p) => ({ ...p, parking_slots: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3">Pricing</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Base Price (in ₹)</label>
                        <input type="number" value={editUnit.base_price || ''} onChange={(e) => setEditUnit((p) => ({ ...p, base_price: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Final Price (in ₹)</label>
                        <input type="number" value={editUnit.final_price || ''} onChange={(e) => setEditUnit((p) => ({ ...p, final_price: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                    </div>
                  </div>

                  {/* Availability & Owner */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3">Availability & Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Status *</label>
                        <select
                          value={editUnit.status || 'available'}
                          onChange={(e) => {
                            const v = (e.target.value || '').toString().toLowerCase();
                            setEditUnit((p) => {
                              const next = { ...p, status: e.target.value };
                              // If status set back to available, clear tenant fields
                              if (v === 'available') {
                                next.tenant_name = '';
                                next.tenant_contact = '';
                                next.tenant_start_date = '';
                                next.tenant_end_date = '';
                              }
                              return next;
                            });
                          }}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="available">Available</option>
                          <option value="hold">On Hold</option>
                          <option value="booked">Booked</option>
                          <option value="sold">Sold</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Keys Location</label>
                        <select value={editUnit.keys_location || ''} onChange={(e) => setEditUnit((p) => ({ ...p, keys_location: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                          <option value="">Select location</option>
                          <option value="on_site">On Site</option>
                          <option value="with_dealer">With Dealer</option>
                          <option value="lost">Lost</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-600">Keys Remarks</label>
                        <input type="text" value={editUnit.keys_remarks || ''} onChange={(e) => setEditUnit((p) => ({ ...p, keys_remarks: e.target.value }))} className="w-full px-3 py-2 border rounded-md" placeholder="e.g., Notes about keys location" />
                      </div>
                    </div>
                  </div>

                  {/* Tenant Information: show when status is booked or sold */}
                  {((editUnit.status || '').toString().toLowerCase() === 'booked' || (editUnit.status || '').toString().toLowerCase() === 'sold') && (
                    <div className="border-b pb-4">
                      <h3 className="font-semibold mb-3">Tenant Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Tenant Name *</label>
                          <input type="text" value={editUnit.tenant_name || ''} onChange={(e) => setEditUnit((p) => ({ ...p, tenant_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600">Contact Number *</label>
                          <input type="text" value={editUnit.tenant_contact || ''} onChange={(e) => setEditUnit((p) => ({ ...p, tenant_contact: e.target.value }))} className="w-full px-3 py-2 border rounded-md" maxLength="15" />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600">Start Date *</label>
                          <input type="date" value={editUnit.tenant_start_date ? editUnit.tenant_start_date.split('T')[0] : ''} onChange={(e) => setEditUnit((p) => ({ ...p, tenant_start_date: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-600">End Date {((editUnit.status || '').toString().toLowerCase() === 'booked') ? '*' : ''}</label>
                          <input type="date" value={editUnit.tenant_end_date ? editUnit.tenant_end_date.split('T')[0] : ''} onChange={(e) => setEditUnit((p) => ({ ...p, tenant_end_date: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                        </div>
                      </div>
                    </div>
                  )}

                  {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
                  <div className="border-b pb-4">
                    <h3 className="font-semibold mb-3">Owner Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Owner Name</label>
                        <input type="text" value={editUnit.owner_name || ''} onChange={(e) => setEditUnit((p) => ({ ...p, owner_name: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Owner Phone</label>
                        <input type="text" value={editUnit.owner_phone || ''} onChange={(e) => setEditUnit((p) => ({ ...p, owner_phone: e.target.value }))} className="w-full px-3 py-2 border rounded-md" maxLength="10" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-600">Owner Email</label>
                        <input type="email" value={editUnit.owner_email || ''} onChange={(e) => setEditUnit((p) => ({ ...p, owner_email: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                    </div>
                  </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => { setEditingUnitId(null); setEditUnit(null); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">Cancel</button>
                  <button onClick={handleEditUnit} disabled={editLoading} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold disabled:opacity-60">{editLoading ? 'Saving...' : 'Update Unit'}</button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* Fullscreen Video Player Modal */}
      {videoModalSrc && (
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
          onClick={() => setVideoModalSrc(null)}
        >
          <div className="relative w-full max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setVideoModalSrc(null)}
              className="absolute -top-10 right-0 p-2 text-white hover:text-red-400 transition-colors z-10"
              title="Close"
            >
              <FiX className="text-2xl" />
            </button>
            <video
              controls
              autoPlay
              className="w-full max-h-[85vh] rounded-xl shadow-2xl bg-black"
            >
              <source src={videoModalSrc} />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      {/* Activity Logs Modal (Managers/Admin Only) */}
      {logsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FiClock className="text-indigo-600" /> Inventory Activity Logs
              </h2>
              <button onClick={() => setLogsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {logsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading logs...</div>
              ) : inventoryLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No activity logs found</div>
              ) : (
                <div className="space-y-3">
                  {inventoryLogs.map((log, idx) => {
                    const actionColors = {
                      UNIT_CREATED: 'bg-green-100 text-green-700',
                      UNIT_UPDATED: 'bg-blue-100 text-blue-700',
                      UNIT_VIEWED: 'bg-gray-100 text-gray-600',
                      STATUS_BOOKED: 'bg-purple-100 text-purple-700',
                      STATUS_HOLD: 'bg-yellow-100 text-yellow-700',
                      STATUS_SOLD: 'bg-red-100 text-red-700',
                      STATUS_AVAILABLE: 'bg-emerald-100 text-emerald-700',
                      MEDIA_UPLOADED: 'bg-cyan-100 text-cyan-700',
                      MEDIA_DELETED: 'bg-orange-100 text-orange-700',
                      PROJECT_CREATED: 'bg-indigo-100 text-indigo-700',
                      TOWER_CREATED: 'bg-teal-100 text-teal-700'
                    };
                    const colorClass = actionColors[log.action] || 'bg-gray-100 text-gray-700';
                    const actionLabels = {
                      UNIT_CREATED: 'Unit Created',
                      UNIT_UPDATED: 'Unit Updated',
                      UNIT_VIEWED: 'Unit Viewed',
                      STATUS_BOOKED: 'Booked',
                      STATUS_HOLD: 'On Hold',
                      STATUS_SOLD: 'Sold',
                      STATUS_AVAILABLE: 'Available',
                      MEDIA_UPLOADED: 'Media Uploaded',
                      MEDIA_DELETED: 'Media Deleted',
                      PROJECT_CREATED: 'Project Created',
                      TOWER_CREATED: 'Tower Created'
                    };
                    const label = actionLabels[log.action] || log.action;
                    const dateStr = log.createdAt
                      ? new Date(log.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
                      : '';
                    return (
                      <div key={log._id || idx} className="bg-gray-50 rounded-lg p-3 border hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colorClass}`}>{label}</span>
                              {log.unitNumber && (
                                <span className="text-sm font-medium text-gray-800">Unit: {log.unitNumber}</span>
                              )}
                              {log.projectName && (
                                <span className="text-sm text-gray-500">({log.projectName}{log.towerName ? ` / ${log.towerName}` : ''})</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{log.details}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              By: <span className="font-medium text-gray-600">{log.employeeName || 'Unknown'}</span>
                              {' • '}{dateStr}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button onClick={() => setLogsModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryPage;
